import React, { type FC, type PropsWithChildren, useCallback, useEffect } from 'react'
import { Box } from '@mui/material'

import type { ArrayType } from '../../../utils'

import {
    ConnectwareError,
    selectIsLoading,
    selectUserManagementSelectedTabData,
    selectUserManagementSelectedTabPage,
    selectUserManagementSelectedTabSearch,
    type Translation,
    type UserManagementTabsTypes,
} from '../../../domain'

import { Table as BaseTable, type TableProps as BaseTableProps, type TableCustomizedState, type TableLine } from '../common'
import { useAppState, useAppUsecase } from '../State'
import { PermissionViewModal } from './Permissions'
import { RoleCreationModal, RoleEditModal } from './Roles'
import { UserCreationModal, UserEditModal } from './Users'

type TableProps<L extends TableLine> =
    & Readonly<{
        selected: keyof UserManagementTabsTypes
        notFound: Translation
    }>
    & Pick<BaseTableProps<L>, 'columns' | 'onRowClick'>

const Table = <L extends TableLine>({
    selected,
    notFound,
    columns,
    onRetry,
    ...props
}: PropsWithChildren<TableProps<L> & Readonly<{ onRetry: VoidFunction }>>): ReturnType<FC> => {
    const data = useAppState((s) => selectUserManagementSelectedTabData(s, selected), [selected])
    const search = useAppState((s) => selectUserManagementSelectedTabSearch(s, selected), [selected])
    const page = useAppState((s) => selectUserManagementSelectedTabPage(s, selected), [selected])
    const isLoading = useAppState(selectIsLoading)
    const loaded = data === null || ConnectwareError.is(data) ? null : data

    const filterUsecase = useAppUsecase('filterUserManagementUsecase')

    const onCustomized = useCallback(
        (s: TableCustomizedState<L>) => {
            if (isLoading) {
                return
            }
            if ('page' in s) {
                filterUsecase.setPage(selected, s.page + 1)
            }
            if ('search' in s) {
                filterUsecase.search(selected, s.search)
            }
        },
        [filterUsecase, selected, isLoading]
    )

    const onRowClick = isLoading ? undefined : props.onRowClick

    return (
        <BaseTable
            columns={columns}
            data={loaded ? (loaded.current as (L & ArrayType<typeof loaded.current>)[]) : []}
            translations={{ emptyTable: notFound }}
            pagination={loaded ? { pageSize: loaded.pageSize, pageSizeOptions: [loaded.pageSize], page: page - 1, totalCount: loaded.totalCount } : undefined}
            onRowClick={onRowClick}
            search={search === null || search}
            loading={data === null}
            error={ConnectwareError.is(data) ? { error: data, onRetry } : undefined}
            onCustomized={onCustomized}
        />
    )
}

export const Page = <L extends TableLine>({ children, ...props }: PropsWithChildren<TableProps<L>>): ReturnType<FC> => {
    const loadUsecase = useAppUsecase('loadUserManagementDataUsecase')
    useEffect(() => loadUsecase.invoke(props.selected), [loadUsecase, props.selected])

    return (
        <Box data-testid="management-table">
            <Table {...props} onRetry={() => loadUsecase.retry(props.selected)} />

            {/* The modals need to be here in order to be shown in every page */}
            <UserEditModal />
            <UserCreationModal />
            <RoleEditModal />
            <RoleCreationModal />
            <PermissionViewModal />
        </Box>
    )
}
